'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { api } from '@/lib/api';
import ResearchTabs from '@/components/ResearchTabs';

/* ------------------------------ types ------------------------------ */

interface SummaryPoint {
  id: string;
  statement: string;
  metricId?: string;
  value?: number;
  unit?: string;
  window?: string;
  asOf: number;
  source: string;
  quality: 'ok' | 'partial' | 'stale' | 'missing';
}
interface AiSummary {
  summaryId: string;
  kind: 'market' | 'signal' | 'study' | 'overview' | 'conclusion';
  title: string;
  points: SummaryPoint[];
  generatedAt: number;
  dataAsOf: number;
  sources: string[];
  limits: string[];
}
interface OverviewResult {
  summary: AiSummary;
  parts: AiSummary[];
}

/* ------------------------------ helpers ------------------------------ */

function fmtTime(t: number | undefined, zh: boolean): string {
  if (!t) return '—';
  const d = new Date(t);
  const now = Date.now();
  const diff = now - t;
  if (diff >= 0 && diff < 60_000) return `${Math.max(0, Math.floor(diff / 1000))}${zh ? ' 秒前' : 's ago'}`;
  if (diff >= 0 && diff < 3_600_000) return `${Math.floor(diff / 60_000)}${zh ? ' 分钟前' : 'm ago'}`;
  if (diff >= 0 && diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}${zh ? ' 小时前' : 'h ago'}`;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const KIND_LABEL: Record<string, { zh: string; en: string }> = {
  market: { zh: '市场快照', en: 'Market snapshot' },
  signal: { zh: '信号', en: 'Signals' },
  study: { zh: '历史验证', en: 'Backtests' },
  conclusion: { zh: 'AI 结论', en: 'AI conclusion' },
};

function fmtKind(kind: string, zh: boolean): string {
  const m = KIND_LABEL[kind];
  return m ? (zh ? m.zh : m.en) : kind;
}

function PointRow({ p, conclusion, zh }: { p: SummaryPoint; conclusion?: boolean; zh: boolean }) {
  return (
    <div className="flex items-start gap-2 border-b border-gf-border/40 py-2 last:border-0">
      <span
        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
          conclusion ? 'bg-gf-accent' : p.quality === 'ok' ? 'bg-green-500' : p.quality === 'partial' ? 'bg-amber-500' : p.quality === 'stale' ? 'bg-red-500' : 'bg-gf-muted'
        }`}
      />
      <div className="min-w-0 flex-1">
        <p className={`text-sm leading-relaxed ${conclusion ? 'font-medium text-gf-accent' : 'text-gf-text'}`}>{p.statement}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-gf-muted">
          <span>⏱ {fmtTime(p.asOf, zh)}</span>
          <span className="rounded bg-gf-border/30 px-1.5 py-0.5">{p.source}</span>
          {p.window ? <span>{zh ? `窗口 ${p.window}` : `Window ${p.window}`}</span> : null}
          {p.quality !== 'ok' ? (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-amber-500">
              {p.quality === 'partial' ? (zh ? '部分' : 'Partial') : p.quality === 'stale' ? (zh ? '过期' : 'Stale') : (zh ? '缺失' : 'Missing')}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ s, zh }: { s: AiSummary; zh: boolean }) {
  const kindLabel = fmtKind(s.kind, zh);
  const isConclusion = s.kind === 'conclusion';
  return (
    <div
      className={`rounded-xl border p-4 ${
        isConclusion ? 'border-gf-accent/50 bg-gf-accent/[0.06]' : 'border-gf-border bg-gf-surface'
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            isConclusion ? 'bg-gf-accent/20 text-gf-accent' : 'bg-gf-accent/10 text-gf-accent'
          }`}
        >
          {kindLabel}
        </span>
        <h3 className={`truncate text-sm font-semibold ${isConclusion ? 'text-gf-accent' : 'text-gf-text'}`}>{s.title}</h3>
      </div>

      {s.points.length > 0 ? (
        <div>
          {s.points.map((p) => (
            <PointRow key={p.id} p={p} conclusion={isConclusion} zh={zh} />
          ))}
        </div>
      ) : (
        <p className="py-3 text-center text-xs text-gf-muted">
          {s.kind === 'market' ? (zh ? '市场快照数据暂缺' : 'Market snapshot data unavailable') : s.kind === 'conclusion' ? (zh ? '暂无可引用的综合结论' : 'No citable conclusion yet') : (zh ? '暂无可引用的确定性结论' : 'No citable deterministic conclusion')}
        </p>
      )}

      {s.limits.length > 0 ? (
        <div className="mt-2 rounded-lg bg-gf-surface2/60 px-3 py-2">
          <p className="mb-1 text-[10px] font-medium text-gf-muted">{zh ? '未生成结论的说明' : 'Why some conclusions were not generated'}</p>
          <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-gf-muted">
            {s.limits.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gf-border/50 pt-2 text-[10px] text-gf-muted">
        <span>{zh ? '数据截止' : 'Data as of'}: {fmtTime(s.dataAsOf, zh)}</span>
        <span>{zh ? '来源' : 'Sources'}: {s.sources.length ? s.sources.join(' / ') : '—'}</span>
      </div>
    </div>
  );
}

/* ------------------------------ page ------------------------------ */

export default function ResearchSummaryPage() {
  const { user, language } = useAppStore();
  const authReady = useRequireAuth('/research/summary', true);
  const zh = language !== 'en';
  const [manualRefresh, setManualRefresh] = useState(0);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['summary', 'overview', language, manualRefresh],
    queryFn: () => api.get<OverviewResult>(`/summary/overview?lang=${language}`),
    staleTime: 30_000,
  });

  const marketCard = data?.parts?.find((p) => p.kind === 'market');
  const signalCards = data?.parts?.filter((p) => p.kind === 'signal') ?? [];
  const studyCards = data?.parts?.filter((p) => p.kind === 'study') ?? [];
  const conclusionCards = data?.parts?.filter((p) => p.kind === 'conclusion') ?? [];

  
  return (
    <main className="mx-auto w-full max-w-[1200px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">
      <EventPing event="research_start" />
      <ResearchTabs />

      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gf-text sm:text-xl">{zh ? 'AI 研究摘要' : 'AI Research Summary'}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">
            {zh ? '仅引用可验证数据与确定性结果；每条结论附时间与来源，缺证据不补写结论。' : 'Only verifiable data and deterministic results are cited; every claim carries time and source, and no conclusion is written without evidence.'}
          </p>
        </div>
        <button
          onClick={() => {
            setManualRefresh((n) => n + 1);
            refetch();
          }}
          disabled={isFetching}
          className="shrink-0 rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-xs font-medium text-gf-text transition-colors hover:bg-gf-border/40 disabled:opacity-50"
        >
          {isFetching ? (zh ? '更新中…' : 'Updating…') : (zh ? '刷新摘要' : 'Refresh')}
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-xl border border-gf-border bg-gf-surface2" />
          ))}
        </div>
      ) : isError || !data ? (
        <div className="rounded-xl border border-gf-border bg-gf-surface p-8 text-center">
          <p className="text-sm text-gf-muted">{zh ? '摘要加载失败，请稍后重试。' : 'Failed to load the summary. Please try again.'}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-xs text-gf-text"
          >
            {zh ? '重试' : 'Retry'}
          </button>
        </div>
      ) : (
        <>
          {/* AI 综合结论（置顶，多维度交叉研判） */}
          {conclusionCards.length > 0 ? (
            <section className="mb-4">
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gf-accent">
                <span className="rounded bg-gf-accent/15 px-1.5 py-0.5 text-[10px] font-medium">AI</span>
                {zh ? 'AI 综合结论' : 'AI Composite Conclusion'}
              </h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {conclusionCards.map((c) => (
                  <SummaryCard key={c.summaryId} s={c} zh={zh} />
                ))}
              </div>
            </section>
          ) : null}

          {/* 市场快照 + 信号 双栏（桌面）/ 单列（手机） */}
          <div className="grid gap-4 lg:grid-cols-2">
            {marketCard ? <SummaryCard s={marketCard} zh={zh} /> : null}
            <div className="flex flex-col gap-4">
              {signalCards.slice(0, 3).length > 0 ? (
                signalCards.slice(0, 3).map((c) => <SummaryCard key={c.summaryId} s={c} zh={zh} />)
              ) : (
                <div className="rounded-xl border border-dashed border-gf-border bg-gf-surface/60 p-6 text-center text-xs text-gf-muted">
                  {zh ? '暂无近期信号摘要' : 'No recent signal summaries'}
                </div>
              )}
            </div>
          </div>

          {/* 历史验证（登录时显示） */}
          {studyCards.length > 0 ? (
            <section className="mt-4">
              <h2 className="mb-2 text-sm font-semibold text-gf-text">
                {zh ? '最近历史验证摘要' : 'Recent backtest summaries'}
                {!user ? (
                  <span className="ml-2 text-[10px] font-normal text-gf-muted">{zh ? '（登入后显示你的研究任务）' : '(log in to see your research jobs)'}</span>
                ) : null}
              </h2>
              <div className="grid gap-4 lg:grid-cols-3">
                {studyCards.map((c) => (
                  <SummaryCard key={c.summaryId} s={c} zh={zh} />
                ))}
              </div>
            </section>
          ) : user ? (
            <div className="mt-4 rounded-xl border border-dashed border-gf-border bg-gf-surface/60 p-6 text-center text-xs text-gf-muted">
              {zh ? '你还没有历史验证任务，去「历史验证」创建一个吧。' : 'No backtest jobs yet — create one in Backtests.'}
            </div>
          ) : null}

          <p className="mt-4 text-[10px] leading-relaxed text-gf-muted">
            {zh ? '摘要为确定性规则引擎生成，仅汇总已验证数据；不构成投资建议，不代表未来表现。' : 'Summaries are generated by a deterministic rule engine and aggregate only verified data; they are not investment advice and do not represent future performance.'}
          </p>
        </>
      )}
    </main>
  );
}
